import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useMemo } from "react";

import { Badge, NotificationType } from "@transfr-inc/dashboard-components";

import { useNotification } from "@hooks/use-notification";
import { Status } from "@src/constants/integration-provider.model";
import { SessionContext } from "@src/context";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";
import {
  getCacheKey,
  getCachedSyncErrors,
  setCachedSyncErrors,
} from "@views/integrations/utils/sync-errors-cache";

import "./sync-badge.scss";

/**
 * Component that displays the sync status badge for an integration connection.
 *
 * This component shows different states:
 * - Loading: Skeleton loader while fetching sync errors
 * - Error: Badge with "View Errors" that opens a modal with error details
 * - Success: Green badge indicating successful sync
 *
 * The component fetches sync errors from the API (with caching) to determine
 * if a "Success" status actually has errors that should be displayed.
 *
 * @param {string|number} connectionId - Connection ID
 * @param {string|number} jobExecutionId - Job execution ID
 * @param {string} status - Status of the last run ("success", "failed", etc.)
 * @param {string} organizationCode - Organization code (optional, falls back to user's org)
 */

const SuccessBadge = () => (
  <Badge
    value={
      <div>
        <FontAwesomeIcon icon={["fa-solid", "circle-check"]} />
        Success
      </div>
    }
    className="badge success"
  />
);

const ErrorBadge = ({ syncErrors, onErrorClick }) => (
  <Badge
    value={
      <div
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onErrorClick(syncErrors);
        }}
      >
        <FontAwesomeIcon icon={["fa-solid", "triangle-exclamation"]} />
        View Errors
      </div>
    }
    className="badge failed"
  />
);

const LoadingSkeleton = () => (
  <div
    className="badge skeleton"
    style={{
      display: "inline-block",
      width: 96,
      height: 20,
      borderRadius: 4,
      background: "linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%)",
      backgroundSize: "400% 100%",
      animation: "syncBadgeSkeleton 1.4s ease infinite",
    }}
  />
);

const SyncBadge = ({
  connectionId,
  jobExecutionId,
  status = Status.FAILED,
  organizationCode,
  onErrorClick,
}) => {
  const { integrationService } = services;
  const { currentUser } = useContext(SessionContext);
  const { organizationCode: userOrgCode } = currentUser || {};
  const effectiveOrgCode = organizationCode || userOrgCode;
  const cacheKey = getCacheKey(effectiveOrgCode, connectionId, jobExecutionId);
  const { addNotification } = useNotification();

  // Fetch sync errors with caching to avoid redundant API calls
  // Errors are cached by connectionId + jobId combination
  const {
    error: syncErrorsError,
    loading: syncErrorsLoading,
    response: syncErrors = [],
    sendRequest: requestSyncErrors,
  } = useApiRequest(async () => {
    try {
      // Check cache first
      const cached = getCachedSyncErrors(cacheKey);
      if (cached) {
        return cached;
      }

      // Fetch from API and cache the result
      const result = await integrationService.getSyncErrors(
        connectionId,
        jobExecutionId,
        effectiveOrgCode,
      );
      setCachedSyncErrors(cacheKey, result);
      return result;
    } catch {
      addNotification({
        type: NotificationType.error,
        message:
          "An error ocurred while fetching sync errors for your connection.",
      });
    }
  }, false);

  useEffect(() => {
    if (syncErrorsError) {
      addNotification({
        type: NotificationType.error,
        message:
          "An error ocurred while fetching sync errors for your connection.",
      });
    }
  }, [syncErrorsError]);

  // Fetch sync errors when connectionId or jobId changes
  useEffect(() => {
    if (connectionId && jobExecutionId) {
      requestSyncErrors();
    }
  }, [connectionId, jobExecutionId, effectiveOrgCode]);

  // Calculate error state based on run status and sync errors
  const errorState = useMemo(() => {
    const errorsExist = syncErrors && syncErrors.length > 0;
    // Error state: either run failed OR run succeeded but has errors
    return (
      status === Status.FAILED || (status === Status.SUCCESS && errorsExist)
    );
  }, [status, syncErrors]);

  // Determine which badge to display based on run status and sync errors
  const renderSyncInfo = () => {
    // Show loading skeleton while fetching errors
    if (syncErrorsLoading) {
      return <LoadingSkeleton />;
    }

    if (errorState) {
      return <ErrorBadge syncErrors={syncErrors} onErrorClick={onErrorClick} />;
    }

    // Success state: run succeeded AND no errors exist
    const isInSuccessState = status === Status.SUCCESS && !syncErrorsLoading;
    if (isInSuccessState) {
      return <SuccessBadge />;
    }
  };

  return <div className="sync-badge">{renderSyncInfo()}</div>;
};

export default SyncBadge;
